import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';

export default function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Users state
  const [users, setUsers] = useState([]);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Agent',
    status: 'Active'
  });

  // Branding state
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchLogoUrl, setBranchLogoUrl] = useState('');
  const [savingLogo, setSavingLogo] = useState(false);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  useEffect(() => {
    (async () => {
      try {
        const [usersRes] = await Promise.all([
          axios.get('/api/users', authHeaders())
        ]);
        setUsers(usersRes.data || []);

        // Load branches (SuperAdmin only route)
        try {
          const branchesRes = await axios.get('/api/branches', authHeaders());
          setBranches(branchesRes.data || []);
          if (branchesRes.data?.[0]?._id) setSelectedBranchId(branchesRes.data[0]._id);
        } catch {
          // If not SuperAdmin or endpoint restricted, show manual entry option
          setBranches([]);
        }
      } catch (err) {
        console.error(err);
        showSnackbar('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load existing logo when branch changes
  useEffect(() => {
    if (!selectedBranchId) return;
    (async () => {
      try {
        const { data } = await axios.get(`/api/settings/branch/${selectedBranchId}`, authHeaders());
        setBranchLogoUrl(data?.logoUrl || '');
      } catch (err) {
        console.error(err);
        setBranchLogoUrl('');
      }
    })();
  }, [selectedBranchId]);

  // Upload helpers
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl('');
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const uploadLogo = async () => {
    if (!selectedBranchId) {
      showSnackbar('Please select a branch first', 'error');
      return;
    }
    if (!selectedFile) {
      showSnackbar('Please choose an image', 'error');
      return;
    }
    setSavingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', selectedFile);
      const { data } = await axios.post(
        `/api/settings/branch/${selectedBranchId}/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token()}`
            // Do not set Content-Type manually; axios sets boundary automatically
          }
        }
      );
      setBranchLogoUrl(data?.logoUrl || '');
      setSelectedFile(null);
      setPreviewUrl('');
      showSnackbar('Logo uploaded successfully');
    } catch (err) {
      console.error(err);
      showSnackbar(err.response?.data?.error || 'Failed to upload logo', 'error');
    } finally {
      setSavingLogo(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));
  const handleTabChange = (_, v) => setActiveTab(v);

  /* --------------------------- Users --------------------------- */
  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'Agent', status: 'Active' });
    setOpenUserDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      status: user.status
    });
    setOpenUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      showSnackbar('Please fill in required fields', 'error');
      return;
    }
    if (!editingUser && !userForm.password) {
      showSnackbar('Password is required for new users', 'error');
      return;
    }
    try {
      const body = { ...userForm };
      if (!body.password) delete body.password;

      if (editingUser) {
        await axios.put(`/api/users/${editingUser._id || editingUser.id}`, body, authHeaders());
        showSnackbar('User updated successfully');
      } else {
        await axios.post('/api/users', body, authHeaders());
        showSnackbar('User created successfully');
      }
      const refreshed = await axios.get('/api/users', authHeaders());
      setUsers(refreshed.data || []);
      setOpenUserDialog(false);
    } catch (err) {
      console.error(err);
      showSnackbar(err.response?.data?.error || 'Failed to save user', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`/api/users/${userId}`, authHeaders());
      showSnackbar('User deleted successfully');
      const refreshed = await axios.get('/api/users', authHeaders());
      setUsers(refreshed.data || []);
    } catch (err) {
      console.error(err);
      showSnackbar(err.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  const formatLastLogin = (date) => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const UserManagement = () => (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>User Management</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser} sx={{ borderRadius: 2 }}>
            Add User
          </Button>
        </Box>

        {users.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>No Users Found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add your first user to get started
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser} sx={{ borderRadius: 2 }}>
              Add User
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {users.map(user => (
              <Box
                key={user._id || user.id}
                sx={{
                  p: 3,
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: user.status === 'Active' ? '#10b981' : '#9e9e9e' }}>
                    {user.name?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{user.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  </Box>
                  <Chip label={user.role} color={user.role === 'SuperAdmin' ? 'warning' : user.role === 'BranchAdmin' ? 'primary' : 'default'} size="small" />
                  <Chip label={user.status} color={user.status === 'Active' ? 'success' : 'default'} size="small" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Last login: {formatLastLogin(user.lastLogin)}
                  </Typography>
                  <IconButton onClick={() => handleEditUser(user)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton
                    onClick={() => handleDeleteUser(user._id || user.id)}
                    size="small"
                    color="error"
                    disabled={user.role === 'SuperAdmin'}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );

  /* --------------------------- Branding --------------------------- */
  const Branding = () => (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
          Branding (Intake Form Logo)
        </Typography>

        <Grid container spacing={3}>
          {/* Branch selector or manual field */}
          <Grid item xs={12} md={6}>
            {branches.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  label="Branch"
                  value={selectedBranchId}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                >
                  {branches.map(b => (
                    <MenuItem key={b._id} value={b._id}>
                      {b.displayName || b.name || b._id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Branch ID"
                fullWidth
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                helperText="Enter the branch _id to save its logo (dropdown shown if /api/branches is available)."
              />
            )}
          </Grid>

          {/* Upload control */}
          <Grid item xs={12} md={6}>
            <input
              id="branch-logo-input"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <label htmlFor="branch-logo-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{ borderRadius: 2 }}
              >
                Choose Logo Image
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Supported: PNG, JPG, JPEG, WEBP, GIF (max 5MB)
            </Typography>
            {selectedFile && (
              <Chip sx={{ mt: 1 }} label={selectedFile.name} variant="outlined" />
            )}
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                border: '1px dashed rgba(0,0,0,0.2)',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{ width: 180, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="New logo preview"
                    style={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }}
                  />
                ) : branchLogoUrl ? (
                  <img
                    src={branchLogoUrl}
                    alt="Current logo"
                    style={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }}
                    onError={(e) => { e.currentTarget.src = ''; }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No logo set</Typography>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                This image appears at the top of the Intake Form for the selected branch.
              </Typography>
            </Box>
          </Grid>

          {/* Upload button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                onClick={uploadLogo}
                disabled={savingLogo || !selectedFile || !selectedBranchId}
                startIcon={savingLogo ? <CircularProgress size={18} sx={{ color: 'white' }} /> : undefined}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {savingLogo ? 'Uploading…' : 'Upload Logo'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const tabs = [<UserManagement key="users" />, <Branding key="branding" />];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 1 }}>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage users and branding.
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Users" />
          <Tab label="Branding" />
        </Tabs>
      </Box>

      {tabs[activeTab]}

      {/* User Dialog */}
      <Dialog
        open={openUserDialog}
        onClose={() => setOpenUserDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            {editingUser ? 'Edit User' : 'Add New User'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3}>
            <TextField
              label="Full Name *"
              fullWidth
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              error={!userForm.name}
              helperText={!userForm.name ? 'Name is required' : ''}
            />
            <TextField
              label="Email Address *"
              type="email"
              fullWidth
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              error={!userForm.email}
              helperText={!userForm.email ? 'Email is required' : ''}
            />
            <TextField
              label={editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
              type="password"
              fullWidth
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              error={!editingUser && !userForm.password}
              helperText={!editingUser && !userForm.password ? 'Password is required for new users' : ''}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                label="Role"
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <MenuItem value="SuperAdmin">SuperAdmin</MenuItem>
                <MenuItem value="BranchAdmin">BranchAdmin</MenuItem>
                <MenuItem value="Agent">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={userForm.status}
                label="Status"
                onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenUserDialog(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveUser}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={!userForm.name || !userForm.email || (!editingUser && !userForm.password)}
          >
            {editingUser ? 'Update' : 'Add'} User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Stack
          component="div"
          sx={{
            bgcolor:
              snackbar.severity === 'error'
                ? '#f44336'
                : snackbar.severity === 'warning'
                ? '#ff9800'
                : '#4caf50',
            color: 'white',
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Stack>
      </Snackbar>
    </Box>
  );
}